<template>
  <div
    class="integration-table-scroll"
    tabindex="0"
    role="region"
    :aria-label="operations ? 'Message operations' : 'Integration logs'"
  >
    <table class="integration-table">
      <colgroup>
        <col v-for="column in columns" :key="column.key" :style="{ width: column.width }" />
      </colgroup>
      <thead>
        <tr>
          <th v-for="column in columns" :key="column.key" scope="col">{{ column.label }}</th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="row in rows"
          :key="row.id"
          tabindex="0"
          @click="open(row, $event)"
          @keydown.enter.self.prevent="open(row, $event)"
          @keydown.space.self.prevent="open(row, $event)"
        >
          <td v-for="column in columns" :key="column.key">
            <div v-if="column.key === 'id'" class="integration-id-cell">
              <span class="integration-ellipsis" v-tooltip="tip(row.id)">
                {{ compactId(row.id) }}
              </span>
              <CopyButton :value="row.id" />
            </div>
            <Status v-else-if="column.key === 'status'" :operation="row" />
            <Status v-else-if="column.key === 'result'" :result="row.result" />
            <span v-else class="integration-ellipsis" tabindex="0" v-tooltip="cellTip(row, column.key)">
              {{ value(row, column.key) }}
            </span>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
import CopyButton from './CopyButton'
import Status from './Status'
import { compactId, dateLabel, tooltip, humanize, recipientLabel, templateLabel, messageText } from './model'
export default {
  components: { CopyButton, Status },
  props: { rows: Array, integration: String, operations: Boolean },
  computed: {
    columns() {
      const definitions = this.operations ? {
        knowledge_ai: [['id', 'ID', '10%'], ['created_at', 'Created', '13%'], ['recipient', 'Session', '14%'], ['account', 'Profile', '9%'], ['content', 'Messages', '18%'], ['source', 'Source', '8%'], ['status', 'Status', '13%'], ['attempt_count', 'Attempts', '5%'], ['last_result', 'Last result', '10%']],
        telegram: [['id', 'ID', '10%'], ['created_at', 'Created', '12%'], ['operation_type', 'Operation', '9%'], ['recipient', 'Chat ID', '9%'], ['account', 'Bot', '8%'], ['content', 'Message', '12%'], ['source', 'Source', '8%'], ['status', 'Status', '15%'], ['attempt_count', 'Attempts', '7%'], ['last_result', 'Last result', '10%']],
        whatsapp: [['id', 'ID', '11%'], ['created_at', 'Created', '12%'], ['recipient', 'Recipient', '10%'], ['provider', 'Provider', '6%'], ['account', 'Account/channel', '9%'], ['content', 'Message/template', '13%'], ['source', 'Source', '8%'], ['status', 'Status', '16%'], ['attempt_count', 'Attempts', '5%'], ['last_result', 'Last result', '10%']],
        email: [['id', 'ID', '12%'], ['created_at', 'Created', '12%'], ['recipient', 'Recipient', '12%'], ['sender', 'Sender', '12%'], ['subject', 'Subject', '14%'], ['source', 'Source', '8%'], ['status', 'Status', '15%'], ['attempt_count', 'Attempts', '5%'], ['last_result', 'Last result', '10%']],
        sendpulse: [['id', 'ID', '11%'], ['created_at', 'Created', '12%'], ['recipient', 'Recipient', '10%'], ['sender', 'Sender', '10%'], ['subject', 'Subject', '10%'], ['template', 'Template', '10%'], ['source', 'Source', '7%'], ['status', 'Status', '15%'], ['attempt_count', 'Attempts', '5%'], ['last_result', 'Last result', '10%']]
      }[this.integration] : {
        knowledge_ai: [['id', 'ID', '12%'], ['created_at', 'Created', '14%'], ['destination', 'Session', '16%'], ['account', 'Profile', '12%'], ['content_preview', 'Messages', '18%'], ['result', 'Result', '12%'], ['http_status', 'HTTP status', '8%'], ['duration_ms', 'Duration (ms)', '8%']],
        telegram: [['id', 'ID', '15%'], ['created_at', 'Created', '16%'], ['method', 'Method', '13%'], ['destination', 'Chat ID', '15%'], ['account', 'Bot', '13%'], ['result', 'Result', '16%'], ['message_id', 'Message ID', '12%']],
        whatsapp: [['id', 'ID', '12%'], ['created_at', 'Created', '14%'], ['method', 'Method', '10%'], ['destination', 'Recipient', '12%'], ['account', 'Account/channel', '12%'], ['content_preview', 'Message/template', '16%'], ['result', 'Result', '12%'], ['provider_ticket_id', 'Ticket ID', '12%']],
        email: [['id', 'ID', '12%'], ['created_at', 'Created', '14%'], ['destination', 'Recipient', '15%'], ['sender', 'Sender', '14%'], ['subject', 'Subject', '17%'], ['result', 'Result', '12%'], ['http_status', 'HTTP status', '8%'], ['provider_message_id', 'Provider ID', '8%']],
        sendpulse: [['id', 'ID', '12%'], ['created_at', 'Created', '14%'], ['destination', 'Recipient', '15%'], ['sender', 'Sender', '13%'], ['subject', 'Subject', '15%'], ['template', 'Template', '12%'], ['result', 'Result', '11%'], ['response_guid', 'Response GUID', '8%']]
      }[this.integration]
      return definitions.map(([key, label, width]) => ({ key, label, width }))
    }
  },
  methods: {
    compactId,
    tip: tooltip,
    cellTip(row, key) {
      const options = tooltip(this.description(row, key))
      if (key === 'content') options.classes += ' integration-tooltip--message'
      return options
    },
    value(row, key) {
      if (key === 'created_at') return dateLabel(row.created_at)
      if (key === 'operation_type') return humanize(row.operation_type)
      if (['recipient', 'destination'].includes(key)) return recipientLabel(row[key])
      if (key === 'account') return row.account?.label || '—'
      if (key === 'source') return row.source?.label || '—'
      if (key === 'template') return templateLabel(row.template)
      if (key === 'content') return messageText(row.content_preview) ?? templateLabel(row.template)
      if (key === 'content_preview') return messageText(row.content_preview) ?? templateLabel(row.template)
      if (key === 'last_result') return row.last_result?.message || row.last_result?.code || '—'
      if (key === 'method') return humanize(row.method)
      return row[key] ?? '—'
    },
    description(row, key) {
      if (key === 'content') return messageText(row.content_preview, true)?.replace(/\n{2,}/g, '\n') ?? templateLabel(row.template)
      if (key === 'created_at') return dateLabel(row.created_at, true)
      if (key === 'content_preview') return messageText(row.content_preview, true)?.replace(/\n{2,}/g, '\n') ?? templateLabel(row.template)
      if (key === 'attempt_count') return row.last_attempt_at ? `Last attempt: ${dateLabel(row.last_attempt_at, true)}` : 'No provider calls recorded'
      if (key === 'recipient' && row.recipient?.items?.length) return row.recipient.items.map(item => [item.label, item.address].filter(Boolean).join(' · ')).join('\n')
      return this.value(row, key)
    },
    open(row, event) {
      if (window.getSelection()?.toString() || event.target.closest('a,button,input,select')) return
      this.$emit('open', row.id, event.currentTarget)
    }
  }
}
</script>
